import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, UsersIcon, BarChartIcon, CalendarIcon, MusicIcon, CheckSquareIcon, TrendingUpIcon, ChevronRightIcon, ChevronLeftIcon, FileTextIcon, MessageSquareIcon } from 'lucide-react';
import { useTheme } from './providers/ThemeProvider';
interface TeacherSidebarProps {
  isExpanded?: boolean;
  isPinned?: boolean;
  onPinToggle?: () => void;
  onArrowToggle?: () => void;
  closeSidebar?: () => void;
}
export const TeacherSidebar: React.FC<TeacherSidebarProps> = ({
  isExpanded = true,
  isPinned = false,
  onPinToggle,
  onArrowToggle,
  closeSidebar
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const currentPath = location.pathname;
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const menuItems = [{
    path: '/teacher-dashboard',
    icon: HomeIcon,
    label: 'Dashboard'
  }, {
    path: '/my-students',
    icon: UsersIcon,
    label: 'Students'
  }, {
    path: '/assigned-tasks',
    icon: CheckSquareIcon,
    label: 'Assigned Tasks'
  }, {
    path: '/reports',
    icon: BarChartIcon,
    label: 'Reports'
  }, {
    path: '/teacher/calendar',
    icon: CalendarIcon,
    label: 'Calendar'
  }, {
    path: '/teacher-sheet-music',
    icon: MusicIcon,
    label: 'Sheet Music'
  }, {
    path: '/teacher-messages',
    icon: MessageSquareIcon,
    label: 'Messages'
  }, {
    path: '/invoices',
    icon: FileTextIcon,
    label: 'Invoices',
    hidden: true // Hidden from sidebar — feature temporarily disabled
  }, {
    path: '/revenue',
    icon: TrendingUpIcon,
    label: 'Revenue'
  }];
  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (focusedIndex === null) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (focusedIndex < menuItems.length - 1) {
          setFocusedIndex(focusedIndex + 1);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (focusedIndex > 0) {
          setFocusedIndex(focusedIndex - 1);
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleNavigation(menuItems[focusedIndex].path);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedIndex, menuItems]);
  const handleNavigation = (path: string) => {
    if (window.innerWidth < 1024 && closeSidebar) {
      closeSidebar();
    }
    navigate(path);
  };
  const handleMenuItemClick = (index: number, path: string) => {
    setFocusedIndex(index);
    handleNavigation(path);
  };
  return <nav className="flex flex-col h-full px-2 py-6" role="navigation" aria-label="Teacher Dashboard Navigation" data-id="element-12137">
      <ul className="space-y-1 flex-1" data-id="element-12138">
        {menuItems.filter(item => !(item as any).hidden).map((item, index) => {
        const Icon = item.icon;
        const isActive = currentPath === item.path || item.path === '/teacher-messages' && currentPath === '/messages';
        const isFocused = focusedIndex === index;
        return <li key={item.path} data-id="element-12139">
                <button onClick={() => handleMenuItemClick(index, item.path)} className={`flex items-center w-full text-left rounded-lg font-medium transition-colors ${isExpanded ? 'px-4 py-3' : 'px-0 py-3 justify-center'} ${isActive || isFocused ? isDark ? 'text-white bg-[#3b82f6]/10' : 'text-gray-900 bg-[#3b82f6]/10' : isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'}`} role="menuitem" aria-current={isActive ? 'page' : undefined} tabIndex={isFocused ? 0 : -1} title={!isExpanded ? item.label : undefined} data-id="element-12140">
                  <Icon className={`h-6 w-6 flex-shrink-0 ${isActive || isFocused ? 'text-[#3b82f6]' : ''}`} data-id="element-12141" />
                  <span className={`text-base whitespace-nowrap overflow-hidden transition-all duration-200 ease-in-out ${isExpanded ? 'ml-3 opacity-100 max-w-[180px]' : 'ml-0 opacity-0 max-w-0'}`} data-id="element-12142">
                    {item.label}
                  </span>
                </button>
              </li>;
      })}
      </ul>

      {/* Arrow toggle button at bottom */}
      <div className={`mt-4 ${isExpanded ? 'px-2' : 'px-0 flex justify-center'}`} data-id="element-12143">
        <button onClick={e => {
        e.stopPropagation();
        onArrowToggle?.();
      }} className={`flex items-center justify-center rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'} ${isExpanded ? 'w-full px-4 py-2.5 gap-2' : 'w-10 h-10'}`} title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} data-id="element-12144">
          {isExpanded ? <>
              <ChevronLeftIcon className="h-5 w-5 flex-shrink-0" data-id="element-12145" />
              <span className="text-sm whitespace-nowrap overflow-hidden" data-id="element-12146">
                Collapse
              </span>
            </> : <ChevronRightIcon className="h-5 w-5" data-id="element-12147" />}
        </button>
      </div>
    </nav>;
};